import {
    ButtonGradientPrimary,
    ButtonSecondary,
    formatDate,
    Label,
    ModalDialog,
    MultilineInput,
    TextInput,
} from '@amelio/ui-library';
import { useEffect, useId, useState } from 'react';

import {
    AGENDA_CONTEXT_MAX_LENGTH,
    AGENDA_DISCUSSION_POINT_TITLE_MAX_LENGTH,
} from '../agendaLimits';

import type { AgendaPointView } from '../agendaViewModel';
import type { FormEvent } from 'react';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';

interface EditDiscussionPointModalProps {
    isOpen: boolean;
    point: AgendaPointView | null;
    onClose: () => void;
    onSave: (title: string, context: string | null) => void;
    isTitleEditable?: boolean;
    dataTestId?: string;
}

export const EditDiscussionPointModal = ({
    isOpen,
    point,
    onClose,
    onSave,
    isTitleEditable = true,
    dataTestId = 'edit-discussion-point-modal',
}: EditDiscussionPointModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const titleId = useId();
    const contextId = useId();
    const [title, setTitle] = useState('');
    const [context, setContext] = useState('');

    useEffect(() => {
        if (point) {
            setTitle(point.title);
            setContext(point.context ?? '');
        }
    }, [point]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        // React carries a submit through the portal to the forms around the agenda: an agenda
        // written inside a form of its own, as a plan's is, would be saved along with the point.
        event.stopPropagation();

        const trimmedTitle = title.trim();
        if (!trimmedTitle) {
            return;
        }

        onSave(trimmedTitle, context.trim() || null);
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('meetingAgenda.editDiscussionPoint')}
            subtitle={
                point
                    ? t('meetingAgenda.dateAdded', {
                          // `dateAdded` is a full instant (`DateTimeOffset` server-side), so it reads
                          // as the instant it is; only its writing belongs to the reader's language,
                          // which `toLocaleDateString` took from the browser instead.
                          date: formatDate({ date: point.dateAdded, language }),
                      })
                    : undefined
            }
            size={'md'}
            closeAriaLabel={t('common.close')}
            onSubmit={handleSubmit}
            backdropBlur
            dataTestId={dataTestId}
            footer={
                <div className={'flex justify-end gap-3'}>
                    <ButtonSecondary
                        size={'sm'}
                        onClick={onClose}
                        dataTestId={`${dataTestId}-cancel-button`}
                    >
                        {t('common.cancel')}
                    </ButtonSecondary>

                    <ButtonGradientPrimary
                        type={'submit'}
                        size={'sm'}
                        dataTestId={`${dataTestId}-save-button`}
                    >
                        {t('meetingAgenda.saveChanges')}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            <div className={'flex flex-col gap-4'}>
                <div className={'flex flex-col gap-2'}>
                    <Label htmlFor={titleId}>{t('meetingAgenda.topicLabel')}</Label>

                    <TextInput
                        id={titleId}
                        value={title}
                        onChange={setTitle}
                        readOnly={!isTitleEditable}
                        maxLength={AGENDA_DISCUSSION_POINT_TITLE_MAX_LENGTH}
                        dataTestId={
                            isTitleEditable
                                ? `${dataTestId}-title-input`
                                : `${dataTestId}-title-readonly`
                        }
                    />
                </div>

                <div className={'flex flex-col gap-2'}>
                    <Label htmlFor={contextId}>{t('meetingAgenda.detailsAndContextLabel')}</Label>

                    <MultilineInput
                        id={contextId}
                        value={context}
                        onChange={setContext}
                        rows={3}
                        maxLength={AGENDA_CONTEXT_MAX_LENGTH}
                        autoGrow
                        dataTestId={`${dataTestId}-context-input`}
                    />
                </div>
            </div>
        </ModalDialog>
    );
};

export default EditDiscussionPointModal;
